/**
 * ContactedPeopleField — selettore "Contattati" nell'header della modale Email (TS-56).
 *
 * Indica a chi è indirizzata la mail scegliendo tra le Persone del CRM. Non ha
 * stato proprio: è una VISTA Person-filtered dello stesso `records` del binder
 * associazioni (vedi useAssociatedRecordsBinder). Il chiamante passa `value`
 * (solo i record di tipo Person) e un `onChange` che rifonde la selezione Person
 * con i record non-Person già presenti: così la selezione qui e il widget
 * "Associazioni" restano sincronizzati automaticamente (unico source-of-truth).
 *
 * La ricerca usa una PROPRIA istanza di useAssociatedRecordsPicker scoping
 * Person: stream indipendente da quello del pannello associazioni, per non
 * contendersi `activeTypeName`.
 *
 * Trigger: nessun contatto → placeholder; un contatto → nome; più contatti →
 * "N contatti". La pre-selezione della Person/dei contatti di contesto arriva
 * gratis dal preview seeding del binder (PersonRelationshipContributor).
 */

import { useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Popover from '@mui/material/Popover';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCurrentGroupId } from '../auth/authSelectors';
import { useAssociatedRecordsPicker, type AssociatedRecord } from './useAssociatedRecordsPicker';

const PERSON_TYPE = 'Person';

const sameRecord = (a: AssociatedRecord, b: AssociatedRecord) =>
  a.typeName === b.typeName && a.id === b.id;

const recordKey = (r: AssociatedRecord) => `${r.typeName}:${r.id}`;

interface ContactedPeopleFieldProps {
  /** Persone attualmente selezionate (sottoinsieme Person di `records`). */
  value: AssociatedRecord[];
  /** Nuova selezione Person; il chiamante la rifonde coi record non-Person. */
  onChange: (people: AssociatedRecord[]) => void;
}

export function ContactedPeopleField({ value, onChange }: ContactedPeopleFieldProps) {
  const { t } = useTranslation();
  const groupId = useCurrentGroupId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { loading, autocompleteProps, listboxProps } = useAssociatedRecordsPicker({
    associableTypeNames: [PERSON_TYPE],
    groupId,
    activeTypeName: PERSON_TYPE,
  });

  const isSelected = (r: AssociatedRecord) => value.some((s) => sameRecord(s, r));

  function toggle(r: AssociatedRecord) {
    if (isSelected(r)) onChange(value.filter((s) => !sameRecord(s, r)));
    else onChange([...value, r]);
  }

  // Righe = selezionati (filtrati client-side sull'input) + risultati di
  // ricerca, deduplicati: i già scelti restano visibili e spuntati in cima.
  const q = autocompleteProps.inputValue.trim().toLowerCase();
  const map = new Map<string, AssociatedRecord>();
  for (const r of value) if (!q || r.label.toLowerCase().includes(q)) map.set(recordKey(r), r);
  for (const o of autocompleteProps.options) map.set(recordKey(o), o);
  const rows = [...map.values()];

  // Testo del trigger: vuoto (mostra il placeholder) con 0 contatti, il nome con
  // 1, il conteggio con più di 1.
  const triggerText =
    value.length === 0
      ? ''
      : value.length === 1
        ? value[0].label
        : t('email.contactedCount', { count: value.length });

  return (
    <>
      {/* Trigger reso come TextField outlined read-only: stesso bordo/altezza/
          label flottante di Direzione e Data → allineamento della riga. */}
      <TextField
        label={t('email.contacted')}
        value={triggerText}
        placeholder={t('email.contactedEmpty')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        fullWidth
        className="form-field"
        sx={{ '& .MuiInputBase-root, & .MuiInputBase-input': { cursor: 'pointer' } }}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <KeyboardArrowDownIcon fontSize="small" />
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true },
        }}
      />

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { className: 'associations-popover-paper' } }}
      >
        <div className="associations-popover associations-popover--single">
          <div className="associations-popover__main">
            <TextField
              autoFocus
              size="small"
              fullWidth
              placeholder={t('email.contactedSearchPlaceholder')}
              value={autocompleteProps.inputValue}
              onChange={(e) => autocompleteProps.onInputChange(e, e.target.value, 'input')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <div
              className="associations-popover__results"
              onScroll={(e) => listboxProps.onScroll(e as unknown as UIEvent<HTMLUListElement>)}
            >
              {rows.length === 0 ? (
                <div className="associations-popover__empty text-muted text-sm">
                  {loading ? t('common.loading') : t('common.noResults')}
                </div>
              ) : (
                rows.map((r) => (
                  <button
                    key={recordKey(r)}
                    type="button"
                    className="associations-popover__row"
                    onClick={() => toggle(r)}
                  >
                    <Checkbox size="small" checked={isSelected(r)} tabIndex={-1} disableRipple />
                    <span className="associations-popover__row-label">
                      {r.label}
                      {r.subtitle && (
                        <span className="contacted-field__email">{r.subtitle}</span>
                      )}
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
}
